import type { CV } from '@shared/types/cv';
import { usePipelineStore } from '@/store/pipeline.store';

export interface DocumentCardProps {
  document: CV;
  type: 'cv' | 'coverLetter';
  onDownload: (id: string) => void;
  onDelete: (id: string) => void;
}

export function DocumentCard({ document, type, onDownload, onDelete }: DocumentCardProps) {
  const jobs = usePipelineStore((s) => s.jobs);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDocumentTitle = () => {
    if (type === 'cv') {
      return document.metadata?.language === 'ru' ? 'CV (Russian)' : 'CV (English)';
    }
    return 'Cover Letter';
  };

  // Look up the job this document was generated for
  const getJobLabel = (): string | null => {
    if (!document.vacancyId) return null;
    const job = jobs.find((j) => j.vacancyId === document.vacancyId);
    if (job) return `${job.title} — ${job.company}`;
    return 'Job removed';
  };

  const jobLabel = getJobLabel();

  return (
    <div className="document-card">
      {/* Preview thumbnail placeholder */}
      <div className="document-card__thumbnail">
        <svg
          className="document-card__thumbnail-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>

      {/* Document info */}
      <div className="document-card__content">
        <h3 className="document-card__title">{getDocumentTitle()}</h3>
        <p className="document-card__date">{formatDate(document.createdAt)}</p>

        {document.vacancyId ? (
          <div className="document-card__badge" title={jobLabel || 'Job-specific'}>
            <svg
              className="document-card__badge-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span className="document-card__badge-text">{jobLabel || 'Job-specific'}</span>
          </div>
        ) : (
          <div className="document-card__badge">
            <span className="document-card__badge-text" style={{ opacity: 0.6 }}>General CV</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="document-card__actions">
        <button
          onClick={() => onDownload(document.id)}
          className="document-card__action-btn document-card__action-btn--download"
          title="Download PDF"
        >
          <svg className="document-card__action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        </button>
        <button
          onClick={() => onDelete(document.id)}
          className="document-card__action-btn document-card__action-btn--delete"
          title="Delete"
        >
          <svg className="document-card__action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
